import { CheckOutlined, EyeOutlined, FilePdfOutlined, MoreOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Form, Input, Modal, notification, Row, Typography, Checkbox, Space } from "antd";
import ListTable from "common/ListTable";
import {
  IOU_COST_DELETE_ENDPOINT,
  IOU_COST_LIST_ENDPOINT,
  IOU_COST_PDF_ENDPOINT,
  IOU_DISBURSE_ENDPOINT,
  IOU_DOCUMENTS_LIST,
  IOU_REQUESTED_BY_USER_ENDPOINT,
  IOU_DOCUMENT_GET_ENDPOINT,
  IOU_DOCUMENTS_REQUESTED_BY_USER,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import AppContent from "common/AppContent";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IOU_DISBURSE_PERMISSION, SALES_CONTRACT_IOU_COST_CREATE_PERMISSION } from "routes/permissions";
import { cglFormatDate, hasPermission, toFixedAndComma } from "utils/functions";
import { checkPermission } from "./config/checkPermission";

export default function IOUDocuments() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;
  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);

  // Get user role from roleInfo
  const roleInfoStr = localStorage.getItem("roleInfo") || "{}";
  let roleInfo;
  try {
    roleInfo = JSON.parse(roleInfoStr);
  } catch (e) {
    roleInfo = {};
  }
  const loggedInUserRole = roleInfo.name || localStorage.getItem("user_type") || "";

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [disburseModalVisible, setDisburseModalVisible] = useState(false);
  const [disburseRecord, setDisburseRecord] = useState(null);
  const [receivedBy, setReceivedBy] = useState("");
  const [disburseForm] = Form.useForm();
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [commentForm] = Form.useForm();

  const { Title } = Typography;

  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllIOUCosts = useCallback(
    async (filterValues) => {
      const { iou_number, status } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      try {
        const query = IOU_DOCUMENT_GET_ENDPOINT;
        const bodyData = {
          ...(iou_number && { iou_number }),
        };

        const response = await getData(query, false, bodyData);
        if (response && (response?.status === 200 || response?.code === 200)) {
          // Handle different response structures
          let documents = [];
          if (Array.isArray(response.data?.data)) {
            documents = response.data.data;
          } else if (Array.isArray(response.data)) {
            documents = response.data;
          } else if (response.data?.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
            documents = response.data.data.data || [];
          }

          const dataWithKeys = documents.map((item, index) => ({
            ...item,
            key: item.id || `iou-document-${index}`,
          }));

          setTableData(dataWithKeys);
          setMetaData({
            current: 1,
            defaultCurrent: 1,
            defaultPageSize: documents.length || 10,
            total: documents.length || 0,
          });
        } else {
          setFetchError("Could not load this list.");
          setTableData([]);
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to fetch IOU documents",
          placement: "topRight",
        });
        setFetchError("Could not load this list.");
        setTableData([]);
      } finally {
        setLoading(false);
      }
    },

    [currentPage],
  );

  useEffect(() => {
    getAllIOUCosts(filterData);
  }, [refresh, filterData, getAllIOUCosts]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedIOUCost = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this IOU Adjustment?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(`${IOU_COST_DELETE_ENDPOINT}/${id}`);

          if (response && (response?.code === 200 || response?.status === 200)) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "IOU Adjustment deleted successfully",
              placement: "topRight",
            });
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/iou-documents-upload/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-documents-upload/edit/${record.id}`);
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-documents-upload/view/${record.id}`);
    },
    [navigate],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);

    if (newSelectedRows.length === 0) {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
      return;
    }

    const allSameStatus =
      newSelectedRows.length > 0 && newSelectedRows.every((row) => row.status === newSelectedRows[0].status);

    if (!allSameStatus) {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
      return;
    }

    // Check permissions for each selected document
    const canRequestStatus = newSelectedRows.some((row) => {
      const isCreator = loggedInUserId === row?.entry_by;
      return checkPermission(
        loggedInUserRole,
        row.status || "created",
        isCreator,
        "request",
        row.department_approve,
        row.director_approve,
        row.account_approve,
      );
    });

    const canApproveStatus = newSelectedRows.some((row) => {
      const isCreator = loggedInUserId === row?.entry_by;
      return checkPermission(
        loggedInUserRole,
        row.status || "created",
        isCreator,
        "approve",
        row.department_approve,
        row.director_approve,
        row.account_approve,
      );
    });

    const canRejectStatus = newSelectedRows.some((row) => {
      const isCreator = loggedInUserId === row?.entry_by;
      return checkPermission(
        loggedInUserRole,
        row.status || "created",
        isCreator,
        "reject",
        row.department_approve,
        row.director_approve,
        row.account_approve,
      );
    });

    setCanRequest(canRequestStatus);
    setCanApprove(canApproveStatus);
    setCanReject(canRejectStatus);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !["created", "requested", "by_department", "rejected", "by_director", "by_account"].includes(record.status),
    }),
  };

  const handleRequestAction = async () => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Warning",
        description: "No documents selected for the request",
        placement: "bottomRight",
      });
      return;
    }

    const selectedDocs = selectedRows;
    const allSameStatus = selectedDocs.length > 0 && selectedDocs.every((doc) => doc.status === selectedDocs[0].status);
    const currentStatus = selectedDocs[0]?.status;
    const isAllowedStatus = currentStatus === "created" || currentStatus === "rejected";

    if (!allSameStatus || !isAllowedStatus) {
      notification.warning({
        message: "Warning",
        description: "Only documents with 'created' or 'rejected' status can be requested.",
        placement: "bottomRight",
      });
      return;
    }

    Modal.confirm({
      title: "Confirm Request",
      content: "Are you sure you want to submit the selected documents for approval?",
      okText: "Yes, Submit",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const requestedIds = selectedRows.map((row) => row.id);

          const payload = {
            requested_id: requestedIds,
            status: "requested",
          };

          const response = await postData(IOU_DOCUMENTS_REQUESTED_BY_USER, payload);

          if (response && (response?.code === 200 || response?.status === 200)) {
            notification.success({
              message: "Success",
              description: response?.message || "Documents requested successfully",
              placement: "bottomRight",
            });

            setSelectedRowKeys([]);
            setSelectedRows([]);
            setCanRequest(false);
            setCanApprove(false);
            setCanReject(false);

            refetch();
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to request documents",
            placement: "bottomRight",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleApproveAction = () => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Warning",
        description: "No documents selected",
        placement: "bottomRight",
      });
      return;
    }

    setActionType("approve");
    setActionModalVisible(true);
    commentForm.resetFields();
  };

  const handleRejectAction = () => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Warning",
        description: "No documents selected",
        placement: "bottomRight",
      });
      return;
    }

    setActionType("reject");
    setActionModalVisible(true);
    commentForm.resetFields();
  };

  const handleActionModalOk = async () => {
    try {
      const values = await commentForm.validateFields();
      const comment = values.comments || "";

      // For reject, comment is required
      if (actionType === "reject" && !comment.trim()) {
        notification.warning({
          message: "Warning",
          description: "Comment is required for rejection.",
          placement: "bottomRight",
        });
        return;
      }

      const selectedDocs = selectedRows;
      if (!selectedDocs.length) return;

      const allSameStatus = selectedDocs.every((doc) => doc.status === selectedDocs[0].status);

      if (!allSameStatus) {
        notification.warning({
          message: "Warning",
          description: "Only documents with the same status can be processed.",
          placement: "bottomRight",
        });
        return;
      }

      const ids = selectedRows.map((row) => row.id);
      const status = actionType === "approve" ? "approved" : "rejected";

      const payload = {
        requested_id: ids,
        status: status,
        comments: comment.trim(),
      };

      const response = await postData(IOU_DOCUMENTS_REQUESTED_BY_USER, payload);

      if (response && (response?.code === 200 || response?.status === 200)) {
        notification.success({
          message: "Success",
          description: response?.message || `Documents ${actionType}d successfully`,
          placement: "bottomRight",
        });

        setSelectedRowKeys([]);
        setSelectedRows([]);
        setCanRequest(false);
        setCanApprove(false);
        setCanReject(false);
        setActionModalVisible(false);
        commentForm.resetFields();

        refetch();
      } else {
        notification.error({
          message: "Error",
          description: `Failed to ${actionType} documents`,
          placement: "bottomRight",
        });
      }
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      notification.error({
        message: "Error",
        description: `Failed to ${actionType} documents`,
        placement: "bottomRight",
      });
    }
  };

  const handleActionModalCancel = () => {
    setActionModalVisible(false);
    setActionType(null);
    commentForm.resetFields();
  };

  const handleDisburseAction = (record) => {
    setDisburseRecord(record);
    setDisburseModalVisible(true);
  };

  const submitDisbursement = async () => {
    if (!receivedBy.trim()) {
      notification.warning({
        message: "Warning",
        description: "Please provide the 'Requested By' field.",
        placement: "topRight",
      });
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("iou_id", disburseRecord.id);
      formData.append("receive_by", receivedBy);

      const response = await postData(IOU_DISBURSE_ENDPOINT, formData);

      if (response && (response?.code === 200 || response?.status === 200)) {
        alertPop("success", response.message || "IOU Adjustment completed successfully");

        refetch(); // Refresh the table data
        setDisburseModalVisible(false);
        setReceivedBy("");
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to disburse IOU",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAction = async (record) => {
    try {
      setLoading(true);

      const response = await getData(
        `${IOU_COST_PDF_ENDPOINT}/${record.id}`,
        false,
        {},
        { "Content-Type": "application/pdf" },
        "blob",
      );

      if (response?.status === 200) {
        const outputFilename = `iou_report_${record.iou_number}.pdf`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response?.data], {
            type: "application/pdf",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
      } else {
        HandleNotification("error", "bottomRight", "Error getting PDF report!", null);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to disburse IOU",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (status) => {
    if (!status) return "-";
    const statusStr = String(status).toLowerCase();
    return statusStr
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const api_base_url = process.env.REACT_APP_BASE_URL;

  const tableColumns = useMemo(
    () => [
      {
        title: "IOU Number",
        dataIndex: ["iou_info", "iou_number"],
        width: "12%",
        key: "iou_number",
        render: (text, record) => record.iou_info?.iou_number || "-",
      },
      {
        title: "IOU For",
        dataIndex: ["iou_info", "iou_for"],
        width: "10%",
        key: "iou_for",
        render: (text, record) => record.iou_info?.iou_for || "-",
      },
      {
        title: "Document Name",
        dataIndex: "document_name",
        width: "20%",
        key: "document_name",
      },
      {
        title: "File",
        key: "file",
        width: "8%",
        render: (_, record) => (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(`${api_base_url}/${record.document}`, "_blank")}
          >
            View
          </Button>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: "12%",
        key: "status",
        render: (status) => {
          let color = "#1890ff";
          let text = formatStatus(status) || "N/A";

          if (status === "requested" || status === "requested_by_user") {
            color = "#fa8c16";
          } else if (status === "created") {
            color = "#1890ff";
          } else if (status === "rejected") {
            color = "#ff4d4f";
          } else if (status === "approved" || status === "approved_by_director") {
            color = "#52c41a";
          } else if (status === "by_department" || status === "approved_by_dept_head") {
            color = "#13c2c2";
          } else if (status === "by_director") {
            color = "#722ed1";
          } else if (status === "by_account") {
            color = "#eb2f96";
          }

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 8px",
                borderRadius: "4px",
                color: "#fff",
                fontSize: "12px",
                textTransform: "capitalize",
                backgroundColor: color,
              }}
            >
              {text}
            </span>
          );
        },
      },
      {
        title: "Comments",
        dataIndex: "comments",
        width: "15%",
        key: "comments",
        render: (text) => text || "-",
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        width: "12%",
        key: "created_at",
        render: (text) => (text ? cglFormatDate(text) : "-"),
      },
      {
        title: "Action",
        key: "action",
        width: "11%",
        render: (_, record) => {
          const menuItems = [
            {
              key: "view",
              label: "View Document",
              icon: <EyeOutlined />,
              onClick: () => window.open(`${api_base_url}/${record.document}`, "_blank"),
            },
          ];

          return (
            <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["hover"]}>
              <Button type="default" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [api_base_url],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllIOUCosts}
        filterName="iou_cost"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <div style={{ display: "flex", alignItems: "center" }}>
                <ExtraContent
                  refresh={refresh}
                  setRefresh={setRefresh}
                  handleAddItemOpen={navigateToAdd}
                  disabled={!hasPermission([SALES_CONTRACT_IOU_COST_CREATE_PERMISSION])}
                  hideAddButton={true}
                  addButtonText={
                    // hasPermission([SALES_CONTRACT_IOU_COST_CREATE_PERMISSION])
                    //   ? "Add New"
                    //   : "No Permission"
                    null
                  }
                  showFilter={showFilter}
                  setShowFilter={setShowFilter}
                />
              </div>
            }
            title={<Title level={5}>IOU Adjustment</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            {(selectedRows.length > 0 && (canRequest || canApprove || canReject)) && (
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end", alignItems: "center", marginRight: "30px", paddingTop: "10px" }}>
                <Space>
                  {canRequest && (
                    <Button type="primary" onClick={handleRequestAction}>
                      Request Approval
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={handleApproveAction}
                      style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                    >
                      Approve
                    </Button>
                  )}
                  {canReject && (
                    <Button
                      type="primary"
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={handleRejectAction}
                    >
                      Reject
                    </Button>
                  )}
                </Space>
              </div>
            )}

            <div className="table" style={{ marginTop: 2 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllIOUCosts(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowSelection={rowSelection}
                rowKey="id"
                emptyText="No records to show yet."
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>

            <Modal
              title="Disbursement Confirmation"
              visible={disburseModalVisible}
              onCancel={() => {
                setDisburseModalVisible(false);
                disburseForm.resetFields(); // Reset the form fields on cancel
              }}
              onOk={() => {
                disburseForm.submit(); // Trigger form submission
                getAllIOUCosts();
              }}
              okText="Disburse"
              cancelText="Cancel"
            >
              <Form
                form={disburseForm}
                layout="vertical"
                onFinish={async (values) => {
                  try {
                    setLoading(true);

                    const formData = new FormData();
                    formData.append("iou_id", disburseRecord.id);
                    formData.append("receive_by", values.receive_by);

                    const response = await postData(IOU_DISBURSE_ENDPOINT, formData);

                    if (response && (response?.code === 200 || response?.status === 200)) {
                      alertPop("success", response.message || "IOU Adjustment completed successfully");

                      refetch(); // Refresh the table data
                      setDisburseModalVisible(false);
                      disburseForm.resetFields(); // Reset the form fields after successful submission
                    }
                  } catch (error) {
                    notification.error({
                      message: "Error",
                      description: "Failed to disburse IOU",
                      placement: "topRight",
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <Form.Item label="IOU Number">
                  <Input value={disburseRecord?.iou_number} disabled />
                </Form.Item>
                <Form.Item label="IOU For">
                  <Input value={disburseRecord?.iou_for} disabled />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="RA">
                      <Input value={toFixedAndComma(disburseRecord?.ra_amount)} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="NRA">
                      <Input value={toFixedAndComma(disburseRecord?.nra_amount)} disabled />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="IOU Amount">
                  <Input value={toFixedAndComma(disburseRecord?.iou_amount)} disabled />
                </Form.Item>
                <Form.Item
                  label="Requested By"
                  name="receive_by"
                  rules={[{ required: true, message: "Please enter the name of the person taking the IOU" }]}
                >
                  <Input placeholder="Requested By" />
                </Form.Item>
              </Form>
            </Modal>

            <Modal
              title={actionType === "approve" ? "Approve Documents" : "Reject Documents"}
              open={actionModalVisible}
              onOk={handleActionModalOk}
              onCancel={handleActionModalCancel}
              okText={actionType === "approve" ? "Approve" : "Reject"}
              okButtonProps={{
                style:
                  actionType === "reject"
                    ? {
                        backgroundColor: "#ff4d4f",
                        borderColor: "#ff4d4f",
                        color: "white",
                      }
                    : {
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                        color: "white",
                      },
              }}
            >
              <Form form={commentForm} layout="vertical">
                <Form.Item
                  name="comments"
                  label="Comments"
                  rules={
                    actionType === "reject"
                      ? [
                          {
                            required: true,
                            message: "Comment is required for rejection.",
                          },
                        ]
                      : []
                  }
                >
                  <Input.TextArea
                    rows={5}
                    placeholder={
                      actionType === "reject" ? "Please provide a reason for rejection" : "Add comments (optional)"
                    }
                  />
                </Form.Item>
              </Form>
            </Modal>
          </>
        }
      />
    </>
  );
}
